import { useCallback, useState } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Input } from 'core-components/library/inputs'
import { TabButtons } from 'core-components/library/tabButtons'
import type { INetworkDebugRequest } from 'shared/lib/networkDebug'

import { CopyIcon, DownloadIcon } from './icons'
import { JsonTree } from './JsonTree'
import {
  copyText,
  formatBytes,
  formatClock,
  formatSeconds,
  formatSize,
  getFileName,
  getProgressPercent,
  getTabValue,
  isContainer,
  saveJson,
} from './lib'
import type { TNetworkDataTab } from './lib'
import { NetworkStatusChip } from './NetworkStatusChip'
import { NetworkTiming } from './NetworkTiming'
import { NETWORK_DEBUG_TRANSLATION_KEYS } from './translations'

import styles from './NetworkRequestDetail.module.scss'

const KEYS = NETWORK_DEBUG_TRANSLATION_KEYS

const TABS: { id: TNetworkDataTab; key: string }[] = [
  { id: 'request', key: KEYS.tabRequest },
  { id: 'response', key: KEYS.tabResponse },
  { id: 'mapped', key: KEYS.tabMapped },
  { id: 'timing', key: KEYS.tabTiming },
]

const Meta = ({ request }: { request: INetworkDebugRequest }) => {
  const body = request.body ? JSON.stringify(request.body) : ''

  return (
    <div className={styles.meta}>
      <div className={styles.metaLine}>
        <span className={styles.code}>
          {request.method} {request.url}
          {body && `  ${body}`}
        </span>
      </div>
      <div className={styles.metaLine}>
        <NetworkStatusChip request={request} />
        {formatSize(request) && <span>{formatSize(request)}</span>}
        {request.durationMs !== null && <span>{formatSeconds(request.durationMs)}</span>}
        <span>
          {formatClock(request.startedAt)}
          {request.silent && (
            <>
              {' · '}
              <Translation text={KEYS.silent} span />
            </>
          )}
        </span>
        <span>
          → <span className={styles.code}>{request.target}</span>
        </span>
      </div>
    </div>
  )
}

const PendingState = ({ request }: { request: INetworkDebugRequest }) => {
  const percent = getProgressPercent(request)
  const { progress } = request

  return (
    <div className={styles.state}>
      <span className={styles.stateTitle}>
        <Translation
          text={KEYS.pendingTitle}
          span
          replaceParams={[{ key: '{name}', value: request.name }]}
        />
      </span>
      {percent !== null && (
        <span className={styles.progress}>
          <i style={{ width: `${percent}%` }} />
        </span>
      )}
      {progress?.totalBytes != null && (
        <Translation
          text={KEYS.pendingProgress}
          span
          replaceParams={[
            { key: '{loaded}', value: formatBytes(progress.loadedBytes) },
            { key: '{total}', value: formatBytes(progress.totalBytes) },
          ]}
        />
      )}
      <Translation text={KEYS.pendingText} span />
    </div>
  )
}

const ErrorState = ({ request }: { request: INetworkDebugRequest }) => (
  <div className={styles.state}>
    <span className={styles.stateTitle}>
      <Translation
        text={KEYS.errorTitle}
        span
        replaceParams={[
          { key: '{name}', value: request.name },
          { key: '{status}', value: String(request.status ?? '—') },
        ]}
      />
    </span>
    <span className={styles.stateText}>
      <Translation
        text={KEYS.errorText}
        span
        replaceParams={[
          { key: '{stage}', value: request.error?.stage ?? '—' },
          { key: '{message}', value: request.error?.message ?? '' },
        ]}
      />
    </span>
    {request.error?.responseText && (
      <pre className={styles.responseText}>{request.error.responseText}</pre>
    )}
  </div>
)

const RequestInfo = ({ request }: { request: INetworkDebugRequest }) => {
  const rows: [string, string][] = [
    [KEYS.method, request.method],
    [KEYS.url, request.url],
    [KEYS.body, request.body ? JSON.stringify(request.body) : '—'],
    [
      KEYS.headers,
      Object.entries(request.headers)
        .map(([name, value]) => `${name}: ${value}`)
        .join('\n') || '—',
    ],
    [KEYS.storedIn, request.target],
    [KEYS.source, request.source ?? '—'],
  ]

  return (
    <table className={styles.info}>
      <tbody>
        {rows.map(([key, value]) => (
          <tr key={key}>
            <th>
              <Translation text={key} span />
            </th>
            <td>{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

interface INetworkRequestDetailProps {
  request: INetworkDebugRequest
}

/** The selected request: what was asked, the response as it came, the data after mapping and
 *  where the time went. */
export const NetworkRequestDetail = ({ request }: INetworkRequestDetailProps) => {
  const t = useSafetyTranslation()
  const [tab, setTab] = useState<TNetworkDataTab>('response')
  const [query, setQuery] = useState('')
  const isTree = tab === 'response' || tab === 'mapped'
  const hasData = request.state === 'ok'

  const copy = useCallback(
    () => copyText(JSON.stringify(getTabValue(request, tab), null, 2), t(KEYS.copied)),
    [request, tab, t]
  )

  const download = useCallback(
    () => saveJson(getTabValue(request, tab), getFileName(request, tab)),
    [request, tab]
  )

  const renderTab = (id: TNetworkDataTab) => {
    if (id === 'request') {
      return <RequestInfo request={request} />
    }

    if (id === 'timing') {
      return <NetworkTiming request={request} />
    }

    if (request.state === 'pending') {
      return <PendingState request={request} />
    }

    if (request.state === 'error') {
      return <ErrorState request={request} />
    }

    const isMapped = id === 'mapped'
    const value = isMapped ? request.mapped : request.raw
    const rootLabel = isMapped ? 'mapped' : 'response'
    // A `{ status, data }` envelope opens straight onto its data.
    const hasDataEnvelope = isContainer(value) && !Array.isArray(value) && isContainer(value.data)

    return (
      <JsonTree
        key={`${request.id}-${id}`}
        value={value}
        rootLabel={rootLabel}
        defaultOpen={hasDataEnvelope ? [`${rootLabel}.data`] : []}
        query={query}
      />
    )
  }

  return (
    <div className={styles.root}>
      <TabButtons
        value={tab}
        onChange={(id) => setTab(id as TNetworkDataTab)}
        classes={{ root: styles.tabs, header: styles.tabsHeader, content: styles.tabsContent }}
        items={TABS.map(({ id, key }) => ({
          id,
          title: () => <Translation text={key} span />,
          content: () => (
            <>
              <Meta request={request} />
              <div className={styles.scroll}>{renderTab(id)}</div>
            </>
          ),
        }))}
        headerContent={() => (
          <div className={styles.toolbar}>
            {isTree && hasData && (
              <Input
                classes={{ root: styles.search }}
                value={query}
                onChange={setQuery}
                startContent={<i className="kbase kbase-search" />}
                placeholder={t(KEYS.search)}
              />
            )}
            <Button variant="contained" color="grey" onClick={copy}>
              <span className={styles.buttonContent}>
                <CopyIcon className={styles.buttonIcon} />
                <Translation text={KEYS.copy} span />
              </span>
            </Button>
            <Button variant="contained" color="grey" onClick={download}>
              <span className={styles.buttonContent}>
                <DownloadIcon className={styles.buttonIcon} />
                <Translation text={KEYS.download} span />
              </span>
            </Button>
          </div>
        )}
      />
    </div>
  )
}

NetworkRequestDetail.displayName = 'NetworkRequestDetail'
